import { Button, ButtonProps, Group, Stack, Text } from "@mantine/core"
import { ReactNode } from "react"

type Tone = "warning" | "danger" | "success" | "info"

type Props = {
  /** Headline question (e.g. "Are you sure you want to approve?") */
  question: ReactNode
  /** Smaller body text below the question explaining the consequence */
  description?: ReactNode
  /** Tone changes the icon halo color (default warning/yellow) */
  tone?: Tone
  /** Override the icon (defaults to WarningIcon) */
  icon?: ReactNode
  /** Cancel button handler — also wired to the implicit Close behavior */
  onCancel: () => void
  cancelLabel?: ReactNode
  /** Confirm handler */
  onConfirm: () => void
  confirmLabel?: ReactNode
  /** Mantine button color for the confirm action; defaults to brand */
  confirmColor?: ButtonProps["color"]
  /** When true, both buttons render as disabled and the confirm shows loading */
  loading?: boolean
  /** Extra content (summary card, reason field) shown above the button row. */
  children?: ReactNode
}

/**
 * Confirm dialog body (canvas OrdB_ReturnDetail modals): left-aligned 18/650 question, 14px
 * description, optional content (summary, reason field), then Болих + the action on the right.
 * No icon halo. `tone` / `icon` are kept for callers but no longer drawn. Use inside a Modal
 * with size="md" (440) and padding 24.
 */
export function ConfirmDialogBody({
  question,
  description,
  onCancel,
  cancelLabel,
  onConfirm,
  confirmLabel,
  confirmColor = "brand",
  loading = false,
  children,
}: Props) {
  return (
    <Stack gap={14}>
      <Stack gap={6}>
        <Text fw={650} fz={18} c="var(--nw-text)">
          {question}
        </Text>
        {description && (
          <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)" lh={1.5}>
            {description}
          </Text>
        )}
      </Stack>

      {children}

      <Group justify="flex-end" gap="xs">
        <Button variant="default" onClick={onCancel} disabled={loading}>
          {cancelLabel ?? "Cancel"}
        </Button>
        <Button color={confirmColor} onClick={onConfirm} loading={loading}>
          {confirmLabel ?? "Confirm"}
        </Button>
      </Group>
    </Stack>
  )
}
